import { ErrorHandler, inject, Injectable } from '@angular/core';
import { TranslateCompiler } from '@ngx-translate/core';
import type { PrimitiveType } from 'intl-messageformat';
import { createTextMessage } from './i18n/icu-message-policy.mjs';

export class TranslationMessageError extends Error {
  readonly language: string;
  readonly path: string;
  readonly phase: 'compile' | 'format';

  /** Adds translation context to the original compilation or formatting error. */
  constructor(language: string, path: string, phase: 'compile' | 'format', cause: unknown) {
    super(`Translation ${phase} failed at ${path} (language ${language})`, { cause });
    this.name = 'TranslationMessageError';
    this.language = language;
    this.path = path;
    this.phase = phase;
  }
}

/**
 * Adapts FormatJS's `intl-messageformat` to ngx-translate's compiler contract.
 * This replaces the former `ngx-translate-messageformat-compiler` ^7.3.0
 * dependency primarily so ICU translations work without CSP's `unsafe-eval`.
 *
 * That package uses `@messageformat/core` to compile messages into JavaScript
 * functions at runtime via the Function constructor. Browsers block this form
 * of code generation unless script-src permits `unsafe-eval`. Both our local
 * development headers (angular.json) and deployed headers
 * (public/staticwebapp.config.json) deliberately omit that permission.
 * `intl-messageformat` parses messages into an abstract syntax tree and
 * interprets it using Intl formatters, without evaluating generated JavaScript.
 * The formatter closures below are ordinary application code, so dynamically
 * loaded translation catalogs can still be compiled under the existing CSP.
 *
 * This adapter also implements the application's translation policy:
 *
 * - Runtime compilation and build/validate-translations.mjs both call
 *   createTextMessage, sharing ICU parsing, locale validation, and rejection of
 *   HTML-like tags and unescaped legacy {{placeholders}}. Catalogs use ICU
 *   {name} arguments, including plural, select, number, date, and time formats.
 * - Formatting accepts primitive values and Date parameters and returns only
 *   text. Objects, arrays, and rich-text callback parameters are rejected;
 *   Date values are supported by date/time formats, not as rich output.
 * - A malformed message or invalid formatting call falls back to its original
 *   source text so other catalog entries remain usable. Angular's ErrorHandler
 *   receives the language, catalog path, compile/format phase, and original
 *   cause. Repeated formatting failures are reported once per compiled entry,
 *   while later calls with valid parameters can still succeed. An error handler
 *   that throws cannot prevent the fallback.
 * - Nested objects and arrays are rebuilt without mutating the loaded catalog,
 *   preserving non-string values and tracking paths for diagnostics. Each
 *   formatter retains its catalog's locale, including fallback-language entries.
 *
 * ngx-translate continues to own loading, merging, language changes, and its
 * service/pipe/directive APIs; this class only replaces message compilation and
 * formatting. Reintroducing the former compiler would require addressing its
 * runtime code generation and preserving the policies above.
 */
@Injectable()
export class IntlMessageFormatCompiler extends TranslateCompiler {
  private readonly errorHandler = inject(ErrorHandler);

  /** Compiles a single translation into a formatter for the requested language. */
  override compile(
    value: string,
    lang: string,
  ): (params?: Record<string, PrimitiveType>) => string {
    return this.compileEntry(value, lang, '<unknown>');
  }

  /** Creates an ICU formatter that reports failures and falls back to the original text. */
  private compileEntry(
    value: string,
    lang: string,
    path: string,
  ): (params?: Record<string, PrimitiveType>) => string {
    let message: ReturnType<typeof createTextMessage>;
    try {
      message = createTextMessage(value, lang);
    } catch (error) {
      this.report(lang, path, 'compile', error);
      return () => value;
    }

    let reported = false;
    const fallback = (cause: unknown): string => {
      if (!reported) {
        reported = true;
        this.report(lang, path, 'format', cause);
      }
      return value;
    };

    return (params?: Record<string, PrimitiveType>): string => {
      try {
        for (const param of Object.values(params ?? {})) {
          if (
            param !== null &&
            param !== undefined &&
            !(param instanceof Date) &&
            !['string', 'number', 'boolean'].includes(typeof param)
          ) {
            return fallback(new TypeError('Only primitive values and Date parameters are supported'));
          }
        }
        const formatted: unknown = message.format(params);
        const parts = Array.isArray(formatted) ? formatted : [formatted];
        if (parts.some((part: unknown) => !['string', 'number', 'boolean'].includes(typeof part))) {
          return fallback(new TypeError('Translation output must be text or a scalar'));
        }
        return parts.map(String).join('');
      } catch (error) {
        return fallback(error);
      }
    };
  }

  /** Compiles all messages in a translation tree for the requested language. */
  override compileTranslations(translations: unknown, lang: string): unknown {
    return this.compileTree(translations, lang, '');
  }

  /** Recursively compiles string entries while preserving structure and tracking error paths. */
  private compileTree(translations: unknown, lang: string, path: string): unknown {
    if (typeof translations === 'string') {
      return this.compileEntry(translations, lang, path || '<unknown>');
    }

    if (Array.isArray(translations)) {
      return translations.map((translation, index) =>
        this.compileTree(translation, lang, `${path}[${index}]`),
      );
    }

    if (translations !== null && typeof translations === 'object') {
      return Object.fromEntries(
        Object.entries(translations).map(([key, translation]) => [
          key,
          this.compileTree(translation, lang, path ? `${path}.${key}` : key),
        ]),
      );
    }

    return translations;
  }

  /** Sends translation errors to Angular's error handler without disrupting the fallback. */
  private report(lang: string, path: string, phase: 'compile' | 'format', cause: unknown): void {
    try {
      this.errorHandler.handleError(new TranslationMessageError(lang, path, phase, cause));
    } catch {
      // A diagnostic handler must not disable the raw-text fallback.
    }
  }
}
